// =================================================================
// useResident hook
//
// Manages the resident's "session" — which is just an accountId in
// localStorage. Provides the current account record (subscribed via
// onValue so admin edits propagate), plus signIn and signOut helpers.
//
// This is intentionally separate from useAuth (which is for Firebase
// Auth admin login). Residents don't authenticate with Firebase —
// they enter a name-based code that maps to an accountId via the
// login_index. The "session" is just remembering that mapping.
//
// Two sign-in entry points:
//   - signIn(rawCode): the resident enters a code like MARGARET-742
//     on /login. We look up the code in login_index and persist the
//     accountId.
//   - signInByDevice(accountId): a device-bundled tablet has been
//     pre-paired to a resident in the admin UI. The tablet arrives
//     at /welcome.html?did=<udid>, the Welcome component looks up
//     the UDID in device_index to get the accountId, then calls
//     this method to establish the session. The login_code is
//     never sent to or known by the client in this flow.
//
// Why this is safe: device_index is a public mapping, but it only
// contains UDID → accountId. The login_code is NOT exposed. An
// attacker who guesses or scrapes a UDID can sign in as that
// resident — which is the same risk as any kiosk-style auth — but
// they can't escalate to learning the login_code that an admin or
// the resident could use elsewhere. The community admin can unpair
// a device at any moment, which immediately revokes that path.
//
// The onValue subscription below auto-cleans the session if the
// account is deleted OR if a permissions error occurs — that means
// unpairing a device from the admin UI causes the tablet to drop
// back to the welcome screen on its next session refresh, without
// any tablet-side action needed.
// =================================================================

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { ref, get, onValue } from 'firebase/database';
import { db } from './firebase.js';
import { normalizeLoginCode } from './loginCode.js';

const ResidentContext = createContext(null);
const STORAGE_KEY = 'brainHealth.residentAccountId';

export function ResidentProvider({ children }) {
  // The accountId is the durable session credential. Reading from
  // localStorage synchronously on mount avoids a flash of "logged
  // out" UI before async storage hydration would settle.
  const [accountId, setAccountId] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || null;
    } catch {
      return null;
    }
  });

  // The full account record from Firebase. null while loading or
  // before sign-in.
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(!!accountId);

  // Subscribe to the resident's account record. Re-runs whenever
  // accountId changes (sign-in, sign-out).
  useEffect(() => {
    if (!accountId) {
      setAccount(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const accountRef = ref(db, `brain_health/accounts/${accountId}`);
    const unsubscribe = onValue(
      accountRef,
      (snap) => {
        const val = snap.val();
        if (val) {
          setAccount({ id: accountId, ...val });
        } else {
          // The accountId in localStorage no longer exists in the
          // database. Probably deleted by admin. Sign out cleanly.
          localStorage.removeItem(STORAGE_KEY);
          setAccountId(null);
          setAccount(null);
        }
        setLoading(false);
      },
      (err) => {
        // Permission error or similar. Sign out so the user can
        // try again rather than getting stuck.
        console.error('Resident account read failed:', err);
        localStorage.removeItem(STORAGE_KEY);
        setAccountId(null);
        setAccount(null);
        setLoading(false);
      }
    );
    return unsubscribe;
  }, [accountId]);

  // Sign in: look up the code in login_index, get the accountId,
  // persist it locally. Throws on failure.
  const signIn = useCallback(async (rawCode) => {
    const code = normalizeLoginCode(rawCode);
    if (!code) {
      throw new Error('Please enter your sign-in code.');
    }

    const indexRef = ref(db, `brain_health/login_index/${code}`);
    const snap = await get(indexRef);
    const foundAccountId = snap.val();

    if (!foundAccountId) {
      // Generic error message — doesn't reveal whether the prefix
      // matched any resident (a tiny defense against code-guessing,
      // and avoids confusing "MARGARET exists but 742 doesn't" UX).
      throw new Error('Code not found.');
    }

    localStorage.setItem(STORAGE_KEY, foundAccountId);
    setAccountId(foundAccountId);
    return foundAccountId;
  }, []);

  // Sign in by accountId directly — used by the device-bundled
  // tablet flow. The caller (Welcome.jsx) has already looked up the
  // UDID in device_index and obtained the accountId; here we just
  // persist it. We deliberately don't validate the accountId exists
  // — the onValue subscription will catch a bad accountId and clear
  // it on the next render, which is the same cleanup the regular
  // signIn flow relies on.
  const signInByDevice = useCallback((foundAccountId) => {
    if (!foundAccountId || typeof foundAccountId !== 'string') {
      throw new Error('Invalid account.');
    }
    localStorage.setItem(STORAGE_KEY, foundAccountId);
    setAccountId(foundAccountId);
    return foundAccountId;
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setAccountId(null);
    setAccount(null);
  }, []);

  const value = {
    accountId,
    account,
    loading,
    isSignedIn: !!accountId,
    signIn,
    signInByDevice,
    signOut,
  };

  return (
    <ResidentContext.Provider value={value}>
      {children}
    </ResidentContext.Provider>
  );
}

export function useResident() {
  const ctx = useContext(ResidentContext);
  if (!ctx) {
    throw new Error('useResident must be used inside <ResidentProvider>');
  }
  return ctx;
}
